import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { jobFiles, jobFileKeys } from "@/lib/job-files";
import type { SendEmailAttachment } from "@/components/email/SendEmailDialog";

export interface JobFilesAttachmentPickerProps {
  open: boolean;
  jobId: number;
  onSelected: (atts: SendEmailAttachment[]) => void;
  onCancel: () => void;
}

export function JobFilesAttachmentPicker({
  open,
  jobId,
  onSelected,
  onCancel,
}: JobFilesAttachmentPickerProps) {
  const { data, isLoading } = useQuery({
    queryKey: jobFileKeys.list(jobId),
    queryFn: () => jobFiles.list(jobId),
    enabled: open,
  });
  const files = data ?? [];
  const [picked, setPicked] = useState<Set<number>>(new Set());

  const togglePicked = (id: number) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleAttach = () => {
    const atts: SendEmailAttachment[] = files
      .filter((f) => picked.has(f.jobFileId))
      .map((f) => ({
        id: String(f.jobFileId),
        name: f.originalFileName,
        source: "library" as const,
      }));
    onSelected(atts);
    setPicked(new Set());
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Attach from job library</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : files.length === 0 ? (
          <p className="text-sm text-muted-foreground">This job has no files yet.</p>
        ) : (
          <ul className="max-h-80 space-y-1 overflow-y-auto">
            {files.map((f) => {
              const checkboxId = `pickfile-${f.jobFileId}`;
              return (
                <li key={f.jobFileId} className="flex items-center gap-2">
                  <Checkbox
                    id={checkboxId}
                    checked={picked.has(f.jobFileId)}
                    onCheckedChange={() => togglePicked(f.jobFileId)}
                  />
                  <Label htmlFor={checkboxId} className="flex-1 cursor-pointer text-sm">
                    {f.originalFileName}
                  </Label>
                  {f.category && <Badge variant="outline">{f.category}</Badge>}
                </li>
              );
            })}
          </ul>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleAttach} disabled={picked.size === 0}>
            Attach selected ({picked.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
